import axios from "axios";
import { syncPostHogOrganization } from "./posthogAnalytics";

const ROUTES_SKIP_SUBSCRIPTION_CHECK = [
  "/login",
  "/register",
  "/onboarding",
  "/subscription-ended",
  "/admin",
];

const IMPERSONATING_KEY = "twilanswer_impersonating";

function isImpersonatingSession() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(IMPERSONATING_KEY) === "1";
}

const CHECKOUT_POLL_DELAYS_MS = [0, 1000, 2000, 3000, 3000, 3000, 4000, 5000];
/** Retries for lockout-page status lookup before showing the unknown fallback. */
const STATUS_RETRY_DELAYS_MS = [0, 800, 2500];

/** How long to trust a successful Stripe verify for the same org + Dynamo sub_status. */
const GOOD_VERIFICATION_TTL_MS = 30_000;

let organizationUpdateListener = null;
/** True while awaitCheckoutSuccessAndUnlock is polling. */
let checkoutUnlockActive = false;
/**
 * Last time check-subscription confirmed active/trialing for a cancelled/unpaid
 * org record. Prevents notify → gate → refetch → notify loops while Dynamo lags.
 * Shape: { orgId, subStatus, at } | null
 */
let lastGoodVerification = null;

function hasCheckoutSuccessQuery() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).get("checkout") === "success";
  } catch {
    return false;
  }
}

export function isCheckoutUnlockInProgress() {
  return checkoutUnlockActive || hasCheckoutSuccessQuery();
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Register a callback to be notified whenever organization is updated (e.g. after
 * fetchAndSetOrganization or after runSubscriptionGate refreshes org). Main.js
 * uses this so its subscription gate state stays in sync.
 */
export function setOrganizationUpdateListener(fn) {
  organizationUpdateListener = fn;
}

export function clearOrganizationUpdateListener() {
  organizationUpdateListener = null;
}

export function notifyOrganizationUpdate(org) {
  syncPostHogOrganization(org);
  if (organizationUpdateListener) {
    organizationUpdateListener(org);
  }
}

function getOrgIdFromOrg(org) {
  if (!org) return null;
  const raw = org.orgId ?? org.org_id;
  return typeof raw === "string" ? raw : raw?.S ?? null;
}

/** Resolve the user's primary org id from DynamoDB-shaped user.relatedOrgs. */
export function getOrgIdFromUser(user) {
  const relatedOrgs = user?.relatedOrgs?.L || [];
  const mainOrg = relatedOrgs.find((o) => o.M?.main?.BOOL === true);
  if (mainOrg) return mainOrg.M.org_id.S;
  if (relatedOrgs.length > 0) return relatedOrgs[0].M.org_id.S;
  return null;
}

function getSubStatus(org) {
  return org?.subscription_details?.M?.sub_status?.S ?? null;
}

/** subscription_details.trial_expires_at (unix seconds), or null when unset. */
export function getTrialExpiresAt(org) {
  const raw = org?.subscription_details?.M?.trial_expires_at?.N;
  if (raw == null || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** Database trial whose end date has passed; the daily sync may not have caught up. */
function isExpiredDatabaseTrial(org) {
  const expiresAt = getTrialExpiresAt(org);
  return (
    getSubStatus(org) === "trial" &&
    expiresAt != null &&
    expiresAt <= Math.floor(Date.now() / 1000)
  );
}

function hasActiveLockoutOverride(org) {
  const lockoutOverride = org?.lockout_override?.M;
  return (
    lockoutOverride?.is_active?.BOOL === true &&
    lockoutOverride?.expiration?.N != null &&
    Number(lockoutOverride.expiration.N) > Math.floor(Date.now() / 1000)
  );
}

function rememberGoodVerification(orgId, subStatus) {
  if (!orgId || subStatus == null) return;
  lastGoodVerification = { orgId, subStatus, at: Date.now() };
}

function wasRecentlyVerifiedGood(orgId, subStatus) {
  if (!lastGoodVerification || !orgId || subStatus == null) return false;
  if (lastGoodVerification.orgId !== orgId) return false;
  if (lastGoodVerification.subStatus !== subStatus) return false;
  return Date.now() - lastGoodVerification.at < GOOD_VERIFICATION_TTL_MS;
}

/**
 * Persist org to localStorage and notify listeners only when the serialized
 * payload changed. Returns true if storage/listeners were updated.
 */
function persistOrganizationIfChanged(org) {
  if (!org) return false;
  const next = JSON.stringify(org);
  const prev =
    typeof window !== "undefined" ? localStorage.getItem("organization") : null;
  if (prev === next) return false;
  localStorage.setItem("organization", next);
  notifyOrganizationUpdate(org);
  return true;
}

/**
 * If org appears cancelled/unpaid, verify with the backend. If still bad, redirect
 * to /subscription-ended. If actually good (e.g. user just renewed), refetch org,
 * update localStorage and listener, and do not redirect.
 * Skip redirect when on auth/subscription-ended routes.
 */
export async function runSubscriptionGate(org, options = {}) {
  const pathname = options.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const subStatus = getSubStatus(org);
  const hasOverride = hasActiveLockoutOverride(org);

  console.log("[Subscription gate] Checking subscription status", {
    pathname,
    subStatusFromOrg: subStatus,
    hasLockoutOverride: hasOverride,
  });

  if (isImpersonatingSession()) {
    console.log("[Subscription gate] Skipping check (support impersonation active)");
    return;
  }

  if (ROUTES_SKIP_SUBSCRIPTION_CHECK.some((route) => pathname.startsWith(route))) {
    console.log("[Subscription gate] Skipping check (route is in skip list)");
    return;
  }

  const isCancelledOrUnpaid =
    subStatus === "cancelled" ||
    subStatus === "canceled" ||
    subStatus === "unpaid" ||
    isExpiredDatabaseTrial(org);
  if (!isCancelledOrUnpaid || hasOverride) {
    console.log("[Subscription gate] No action needed", {
      isCancelledOrUnpaid,
      hasOverride,
    });
    return;
  }

  const orgId = getOrgIdFromOrg(org);
  const subDetails = org?.subscription_details?.M;
  const customerId = subDetails?.stripe_cust_id?.S;
  if (!orgId || !customerId) {
    console.info("[Auth] Redirecting to subscription-ended: org or Stripe customer id is missing (account doesn't have valid org/subscription).");
    window.location.href = "/subscription-ended";
    return;
  }

  if (wasRecentlyVerifiedGood(orgId, subStatus)) {
    console.log("[Subscription gate] Skipping re-verify; recently confirmed active/trialing for this org and sub_status", {
      orgId,
      subStatus,
    });
    return;
  }

  console.log("[Subscription gate] Verifying with backend (check-subscription)", { orgId });
  try {
    const subRes = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
      {
        organization_id: orgId,
        customer_id: customerId,
      }
    );
    const subscriptionStatus = subRes?.data?.subscriptionStatus ?? null;
    const isGood = subscriptionStatus === "active" || subscriptionStatus === "trialing";

    console.log("[Subscription gate] Backend subscription status", {
      subscriptionStatus,
      isGood,
    });

    if (isGood) {
      console.log("[Subscription gate] Subscription active/trialing, refreshing org and allowing access");
      // Record before persist/notify so a re-entrant gate from notify cannot loop.
      rememberGoodVerification(orgId, subStatus);
      const orgRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "get-organization",
        { orgId }
      );
      const updatedOrg = orgRes?.data?.organization;
      if (updatedOrg) {
        persistOrganizationIfChanged(updatedOrg);
      }
      return;
    }
  } catch (err) {
    console.error("[Subscription gate] check-subscription failed", err?.response?.data ?? err?.message ?? err);
  }

  if (options.skipRedirect || isCheckoutUnlockInProgress()) {
    console.log("[Subscription gate] Still cancelled/unpaid but skipRedirect/checkout-unlock set, not redirecting");
    return;
  }
  console.info("[Auth] Redirecting to subscription-ended: subscription is cancelled/unpaid and backend verification confirmed.");
  window.location.href = "/subscription-ended";
}

/**
 * After Stripe Checkout success (`/dashboard?checkout=success`), poll
 * check-subscription until active/trialing, refresh org, and clear the query.
 * Returns { activated, timedOut }. On timeout redirects to subscription-ended.
 */
export async function awaitCheckoutSuccessAndUnlock() {
  if (typeof window === "undefined" || !hasCheckoutSuccessQuery()) {
    return { activated: false, timedOut: false };
  }

  const orgJson = localStorage.getItem("organization");
  if (!orgJson) {
    window.location.href = "/subscription-ended?checkout=pending";
    return { activated: false, timedOut: true };
  }

  let org;
  try {
    org = JSON.parse(orgJson);
  } catch {
    window.location.href = "/subscription-ended?checkout=pending";
    return { activated: false, timedOut: true };
  }

  const orgId = getOrgIdFromOrg(org);
  const customerId = org?.subscription_details?.M?.stripe_cust_id?.S;
  if (!orgId || !customerId) {
    window.location.href = "/subscription-ended?checkout=pending";
    return { activated: false, timedOut: true };
  }

  checkoutUnlockActive = true;
  console.log("[Checkout unlock] Starting subscription poll after Checkout success", { orgId });

  try {
    for (let i = 0; i < CHECKOUT_POLL_DELAYS_MS.length; i++) {
      const delay = CHECKOUT_POLL_DELAYS_MS[i];
      if (delay > 0) await sleep(delay);

      try {
        const subRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
          {
            organization_id: orgId,
            customer_id: customerId,
          }
        );
        const subscriptionStatus = subRes?.data?.subscriptionStatus ?? null;
        const isGood =
          subscriptionStatus === "active" || subscriptionStatus === "trialing";
        console.log("[Checkout unlock] Poll result", {
          attempt: i + 1,
          subscriptionStatus,
          isGood,
        });

        if (!isGood) continue;

        // Seed gate memo so post-unlock notify cannot re-verify into a request loop
        // while Dynamo still shows canceled.
        rememberGoodVerification(orgId, getSubStatus(org));

        const orgRes = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "get-organization",
          { orgId }
        );
        const updatedOrg = orgRes?.data?.organization;
        if (updatedOrg) {
          persistOrganizationIfChanged(updatedOrg);
        }

        const url = new URL(window.location.href);
        url.searchParams.delete("checkout");
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);

        console.log("[Checkout unlock] Subscription active, unlocked dashboard");
        return { activated: true, timedOut: false };
      } catch (err) {
        console.error(
          "[Checkout unlock] Poll failed",
          err?.response?.data ?? err?.message ?? err
        );
      }
    }

    console.info("[Checkout unlock] Timed out waiting for active subscription");
    window.location.href = "/subscription-ended?checkout=pending";
    return { activated: false, timedOut: true };
  } finally {
    checkoutUnlockActive = false;
  }
}

/**
 * Fetch Stripe subscription status with short retries. Does not redirect.
 * Callers must pass a real orgId + customerId — missing billing identity is a
 * known "never subscribed" state, not a failed lookup; do not treat that as
 * resolved: false (which means "we tried and failed").
 * @returns {{ resolved: boolean, status: string|null }}
 */
export async function fetchStripeSubscriptionStatus(orgId, customerId) {
  if (!orgId || !customerId) {
    // Defensive: missing IDs are not a network failure. Prefer handling this
    // upstream (e.g. SubscriptionEnded → "ended") rather than as unknown.
    return { resolved: false, status: null };
  }

  let lastErr = null;
  for (const delay of STATUS_RETRY_DELAYS_MS) {
    if (delay) await sleep(delay);
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
        {
          organization_id: orgId,
          customer_id: customerId,
        }
      );
      return {
        resolved: true,
        status: res?.data?.subscriptionStatus ?? null,
      };
    } catch (err) {
      lastErr = err;
      console.error(
        "[Subscription gate] check-subscription attempt failed",
        err?.response?.data ?? err?.message ?? err
      );
    }
  }
  console.error(
    "[Subscription gate] check-subscription exhausted retries",
    lastErr?.response?.data ?? lastErr?.message ?? lastErr
  );
  return { resolved: false, status: null };
}

/**
 * Re-check subscription with Stripe (check-subscription) and if active/trialing,
 * refresh org, update storage/listener, and redirect to /dashboard. Used on the
 * subscription-ended page so users who just renewed can get back in without logging out.
 * Reads org from localStorage. Retries transient failures before giving up.
 * @returns {{ redirected: boolean, subscriptionStatus: string|null, resolved: boolean }}
 */
export async function recheckSubscriptionAndRedirectIfActive() {
  const empty = { redirected: false, subscriptionStatus: null, resolved: false };
  const orgJson = typeof window !== "undefined" ? localStorage.getItem("organization") : null;
  if (!orgJson) return empty;
  let org;
  try {
    org = JSON.parse(orgJson);
  } catch {
    return empty;
  }
  const orgId = getOrgIdFromOrg(org);
  const subDetails = org?.subscription_details?.M;
  const customerId = subDetails?.stripe_cust_id?.S;
  if (!orgId || !customerId) return empty;

  console.log("[Subscription gate] Rechecking subscription (from subscription-ended page)", { orgId });
  const { resolved, status: subscriptionStatus } =
    await fetchStripeSubscriptionStatus(orgId, customerId);

  if (!resolved) {
    return empty;
  }

  const isGood =
    subscriptionStatus === "active" || subscriptionStatus === "trialing";
  console.log("[Subscription gate] Recheck result", { subscriptionStatus, isGood });

  if (!isGood) {
    return { redirected: false, subscriptionStatus, resolved: true };
  }

  console.log("[Subscription gate] Subscription active/trialing, refreshing org and redirecting to dashboard");
  rememberGoodVerification(orgId, getSubStatus(org));
  try {
    const orgRes = await axios.post(
      process.env.REACT_APP_BACKEND_URL + "get-organization",
      { orgId }
    );
    const updatedOrg = orgRes?.data?.organization;
    if (updatedOrg) {
      persistOrganizationIfChanged(updatedOrg);
    }
  } catch (err) {
    console.error(
      "[Subscription gate] get-organization after active recheck failed",
      err?.response?.data ?? err?.message ?? err
    );
  }
  window.location.href = "/dashboard";
  return { redirected: true, subscriptionStatus, resolved: true };
}

/**
 * Fetch organization by id, store in localStorage, notify listener, and run the
 * subscription gate (verify cancelled/unpaid with backend before redirecting).
 * Returns the fetched org, or throws on request failure.
 */
export async function fetchAndSetOrganization(orgId) {
  const res = await axios.post(
    process.env.REACT_APP_BACKEND_URL + "get-organization",
    { orgId }
  );
  const org = res?.data?.organization;
  if (!org) {
    throw new Error("get-organization did not return organization");
  }
  localStorage.setItem("organization", JSON.stringify(org));
  notifyOrganizationUpdate(org);
  console.log("[Subscription gate] Running gate after fetchAndSetOrganization", { orgId });
  await runSubscriptionGate(org);
  return org;
}
